import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { FaEdit, FaTrash, FaEye, FaTimes } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { canViewCustomerDocument, isCustomerDocumentImage, resolveCustomerDocumentUrl, fetchAuthenticatedDocumentBlob } from './customerDocumentHelpers';

const MAX_FILE_SIZE_MB = 200;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const DocumentDetails = ({ setDocumentsList, documentsList, documentType, readOnly = false }) => {
  const { t } = useTranslation();
  const initialState = {
    type: null,
    IssueDate: null,
    documentNo: '',
    file: null,
    expiryDate: null,
    Remarks: '',
    verified: false,
  };

  const [formData, setFormData] = useState(initialState);
  const [editIndex, setEditIndex] = useState(null);
  // Preview now renders inline in a panel under the grid (was a popup modal /
  // new-tab window.open, which read as "nothing happens" behind a popup
  // blocker or a background tab) - one object holds the resolved url plus
  // whether it's an image (inline <img>) or not (embedded via <iframe>, which
  // also renders PDFs natively) and whether the url is a blob: that needs
  // revoking when swapped out or the component unmounts.
  const [viewingDocument, setViewingDocument] = useState(null);
  // Guards against a slow first fetch resolving after a second "View" click
  // already moved on to a different document - only the latest request's
  // result is allowed to update state.
  const viewRequestId = useRef(0);

  useEffect(() => {
    return () => {
      if (viewingDocument?.isBlob && viewingDocument?.url) {
        URL.revokeObjectURL(viewingDocument.url);
      }
    };
  }, [viewingDocument]);

  const closePreview = () => {
    if (viewingDocument?.isBlob && viewingDocument?.url) {
      URL.revokeObjectURL(viewingDocument.url);
    }
    setViewingDocument(null);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      const file = files[0];
      if (file && file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(t('common.fileTooLarge', `File exceeds the ${MAX_FILE_SIZE_MB}MB limit and was not added.`));
        return;
      }
      setFormData((prev) => ({ ...prev, file }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (selected) => {
    setFormData((prev) => ({ ...prev, type: selected }));
  };
  const handleDateChange = (input) => {
    const value = input?.target?.value || input.value;
    const name = input?.target?.name || input.name;
    const updatedValue = typeof value === 'string' ? value : formatDate(value);
    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
  };

  const handleAddOrUpdate = () => {
    if (editIndex !== null) {
      const updated = [...documentsList];
      updated[editIndex] = formData;
      setDocumentsList(updated);
      setEditIndex(null);
    } else {
      setDocumentsList((prev) => [...prev, formData]);
    }

    setFormData(initialState);
  };

  const handleEdit = (index) => {
    setFormData({
      ...documentsList[index],
      file: null, // file input cannot be prefilled
    });
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    setDocumentsList(documentsList.filter((_, i) => i !== index));
  };

  const handleView = async (data) => {
    const rawUrl = resolveCustomerDocumentUrl(data);
    if (!rawUrl) return;

    // Swapping to a new document - revoke the previous blob url (if any)
    // right away rather than waiting for the effect cleanup on the next
    // render, so blob memory doesn't pile up if the user clicks through
    // several documents quickly.
    if (viewingDocument?.isBlob && viewingDocument?.url) {
      URL.revokeObjectURL(viewingDocument.url);
    }

    const requestId = ++viewRequestId.current;
    const label = data?.type?.label || data?.documentNo || '';
    const isImage = isCustomerDocumentImage(data, rawUrl);

    // A file just picked in this form (not yet uploaded) already has a
    // local blob url from resolveCustomerDocumentUrl - nothing to fetch.
    if (data?.file instanceof File) {
      setViewingDocument({ url: rawUrl, isImage, isBlob: true, label, loading: false, error: false, rawUrl });
      return;
    }

    // Already-saved documents come from the backend - src="rawUrl" on an
    // <img>/<iframe> is a bare browser request with none of our app's auth
    // headers, so it 401s silently and renders blank. Fetch it the same
    // authenticated way the rest of the app does, then hand the frame a
    // local blob: url instead.
    setViewingDocument({ url: null, isImage, isBlob: false, label, loading: true, error: false, rawUrl });
    try {
      const blob = await fetchAuthenticatedDocumentBlob(rawUrl);
      if (viewRequestId.current !== requestId) {
        // A newer "View" click already superseded this one - drop the
        // result instead of clobbering whatever's now on screen.
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      setViewingDocument({ url: blobUrl, isImage: isImage || blob.type?.startsWith('image/'), isBlob: true, label, loading: false, error: false, rawUrl });
    } catch (err) {
      console.error('Error fetching document preview:', err);
      if (viewRequestId.current !== requestId) return;
      setViewingDocument({ url: null, isImage, isBlob: false, label, loading: false, error: true, rawUrl });
    }
  };

  const formatDate = (date) => {
    if (!date || date === '0') return '--';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 1900) return '--';
    return parsed.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="cost-center-section my-3">
      <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
        {!readOnly && (
          <>
            <div className="form-group grid-contain ">
              <div className="d-flex gap-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="taxInclusive"
                  style={{ width: '20px', height: '20px' }}
                  checked={formData.verified}
                  onChange={(e) => setFormData((prev) => ({ ...prev, verified: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="taxInclusive">
                  {t('customerMaster.documentDetailsFields.documentVerified')}
                </label>
              </div>
              <div></div>
              <div></div>

              <div className="d-flex">
                <label className="form-label pt-1 start">{t('customerMaster.documentDetailsFields.documentType')}</label>
                <Select
                  className="p-0 w-100"
                  menuPortalTarget={document.body}
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 5,
                    padding: 9,
                  })}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
                      color: state.isFocused ? '#000 ' : '#000',
                      ':hover': {
                        backgroundColor: '#F5F5F5',
                        color: '#000 ',
                      },
                    }),
                  }}
                  onChange={handleSelectChange}
                  options={documentType?.map((country) => ({ value: country?.value, label: country?.text }))}
                  value={formData.type}
                />
              </div>

              <div className="d-flex">
                <label className="form-label pt-1 start">{t('customerMaster.documentDetailsFields.issueDate')} </label>
                <DatePicker
                  selected={formData.IssueDate}
                  onChange={(date) => handleDateChange({ name: 'IssueDate', value: date })}
                  dateFormat="dd-MMM-yyyy"
                  className="p-2 form-control w-100"
                />
              </div>

              <div className="d-flex">
                <label className="form-label pt-1 start">{t('customerMaster.documentDetailsFields.documentNo')} </label>
                <input type="text" className="p-2 form-control w-75" name="documentNo" value={formData.documentNo} onChange={handleInputChange} />
              </div>

              <div className="d-flex">
                <label className="form-label pt-1 start">{t('customerMaster.documentDetailsFields.file')}</label>
                <input type="file" className="p-2 form-control w-75" name="file" onChange={handleInputChange} />
              </div>

              <div className="d-flex">
                <label className="form-label pt-1 start">{t('customerMaster.documentDetailsFields.expiryDate')} </label>
                <DatePicker
                  selected={formData.expiryDate}
                  onChange={(date) => handleDateChange({ name: 'expiryDate', value: date })}
                  dateFormat="dd-MMM-yyyy"
                  className="p-2 form-control w-100"
                />
              </div>

              <div className="d-flex">
                <label className="form-label pt-1 start">{t('common.remarks')}</label>
                <input type="text" className="p-2 form-control w-75" name="Remarks" value={formData.Remarks} onChange={handleInputChange} />
              </div>
            </div>

            <div className="my-4">
              <div className="mainbtnSecondary flex-end gap-2 col-2" onClick={handleAddOrUpdate}>
                <FaPlus color="#fff" className="center mt-1 mx-2" />
                <div className="center">{editIndex !== null ? t('customerMaster.documentDetailsFields.updateFile') : t('customerMaster.documentDetailsFields.addFile')}</div>
              </div>
            </div>
          </>
        )}

        <div className="table-container" style={{ overflowX: 'auto', height: '400px' }}>
          <table className="table table-bordered gridshad">
            <thead className="dynoTable">
              <tr>
                <th>{t('customerMaster.documentDetailsFields.documentType')}</th>
                <th>{t('customerMaster.documentDetailsFields.documentNo')}</th>
                <th>{t('customerMaster.documentDetailsFields.expiryDate')}</th>
                <th>{t('customerMaster.documentDetailsFields.issueDate')}</th>
                <th>{t('common.remarks')}</th>
                <th style={{ minWidth: '110px', textAlign: 'center' }}>{t('common.view')}</th>
                {!readOnly && <th style={{ minWidth: '90px', textAlign: 'center' }}>{t('customerMaster.contactDetailsFields.action')}</th>}
              </tr>
            </thead>
            <tbody>
              {documentsList.map((data, index) => (
                <tr key={index}>
                  <td>{data.type?.label}</td>
                  <td>{data.documentNo}</td>
                  <td>{formatDate(data.expiryDate)}</td>
                  <td>{formatDate(data.IssueDate)}</td>
                  <td>{data.Remarks}</td>
                  <td className="align-middle text-center" style={{ minWidth: '110px' }}>
                    <div
                      className={`mainbtnRed flex-end gap-2 mx-auto ${canViewCustomerDocument(data) ? '' : 'opacity-50 pe-none'}`}
                      style={{ width: 'fit-content', minWidth: '88px', cursor: canViewCustomerDocument(data) ? 'pointer' : 'not-allowed' }}
                      onClick={() => canViewCustomerDocument(data) && handleView(data)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (canViewCustomerDocument(data) && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleView(data);
                        }
                      }}
                    >
                      <FaEye color="#fff" className="center mt-1 mx-2" />
                      <div className="center">{t('common.view')}</div>
                    </div>
                  </td>
                  {!readOnly && (
                    <td className="align-middle text-center" style={{ minWidth: '90px' }}>
                      <div className="d-flex justify-content-center align-items-center gap-3">
                        <FaEdit size={20} onClick={() => handleEdit(index)} style={{ cursor: 'pointer' }} title={t('common.edit')} />
                        <FaTrash size={20} color="red" onClick={() => handleDelete(index)} style={{ cursor: 'pointer' }} title={t('common.delete')} />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {viewingDocument && (
          <div className="mt-3 p-3" style={{ border: '1px solid #E5E7EB', borderRadius: '8px', background: '#FAFAFA' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>
                {t('customerMaster.documentDetailsFields.documentPreview')}
                {viewingDocument.label ? ` - ${viewingDocument.label}` : ''}
              </strong>
              <FaTimes onClick={closePreview} style={{ cursor: 'pointer' }} title={t('common.close') || 'Close'} />
            </div>
            {viewingDocument.loading && <div className="text-center p-4">{t('common.loading')}</div>}
            {viewingDocument.error && (
              <div className="text-center p-4">
                <div className="mb-2">{t('customerMaster.documentDetailsFields.previewFailed')}</div>
                {viewingDocument.rawUrl && (
                  <a href={viewingDocument.rawUrl} target="_blank" rel="noopener noreferrer">
                    {t('customerMaster.documentDetailsFields.openInNewTab')}
                  </a>
                )}
              </div>
            )}
            {!viewingDocument.loading && !viewingDocument.error && viewingDocument.url && (
              <>
                {viewingDocument.isImage ? (
                  <img src={viewingDocument.url} alt="Document preview" style={{ maxWidth: '100%', maxHeight: '600px', display: 'block', margin: '0 auto' }} />
                ) : (
                  <iframe src={viewingDocument.url} title="Document preview" style={{ width: '100%', height: '600px', border: 'none' }} />
                )}
                {viewingDocument.rawUrl && (
                  <div className="text-center mt-2">
                    <a href={viewingDocument.rawUrl} target="_blank" rel="noopener noreferrer">
                      {t('customerMaster.documentDetailsFields.openInNewTab')}
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentDetails;
